import { apiClient } from "@/lib/axios"
import { getPage, type PagedResponse } from "@/lib/api"
import { getAccessToken, isAccessTokenExpired, renovarToken } from "@/lib/auth"

export interface Notificacao {
  id: string
  titulo: string
  corpo: string | null
  entidadeTipo: string | null
  entidadeId: string | null
  status: string | null
  criadaEm: string
  lidaEm: string | null
}

const BASE = "/api/v1/notificacoes"
// As notificações in-app são gravadas no schema do módulo que as produz
// (hoje, sigi-forms). Por isso a caixa consulta o serviço de formulários.
const FORMS_ORIGIN = (import.meta.env.VITE_FORMS_URL ?? "").replace(/\/+$/, "")
const OPT = { baseURL: FORMS_ORIGIN }

export async function listarNotificacoes(
  page = 0,
  size = 20,
): Promise<PagedResponse<Notificacao>> {
  return getPage<Notificacao>(BASE, { page, size }, OPT)
}

export async function contarNaoLidas(): Promise<number> {
  const { data } = await apiClient.get<{ total: number }>(`${BASE}/nao-lidas`, OPT)
  return data.total
}

export async function marcarNotificacaoLida(id: string): Promise<Notificacao> {
  const { data } = await apiClient.put<Notificacao>(`${BASE}/${id}/ler`, undefined, OPT)
  return data
}

export type TipoEventoNotificacao = "nova" | "atualizada"

/**
 * Conecta ao stream SSE de notificações. Usa `fetch` (e não `EventSource`)
 * para enviar o Bearer token no cabeçalho Authorization.
 */
export async function assinarNotificacoes(
  signal: AbortSignal,
  aoReceber: (notificacao: Notificacao, tipo: TipoEventoNotificacao) => void,
): Promise<void> {
  let token = getAccessToken()
  if (!token || isAccessTokenExpired()) {
    const renovado = await renovarToken()
    if (!renovado) throw new Error("Sessão expirada.")
    token = getAccessToken()
  }

  const resposta = await fetch(`${FORMS_ORIGIN}${BASE}/stream`, {
    signal,
    credentials: "include",
    headers: {
      Accept: "text/event-stream",
      Authorization: `Bearer ${token}`,
    },
  })
  if (!resposta.ok || !resposta.body) {
    throw new Error(`Não foi possível conectar ao push (${resposta.status}).`)
  }

  const leitor = resposta.body.getReader()
  const decodificador = new TextDecoder()
  let buffer = ""

  while (true) {
    const { value, done } = await leitor.read()
    if (done) break
    buffer = (buffer + decodificador.decode(value, { stream: true })).replace(/\r\n/g, "\n")

    let separador = buffer.indexOf("\n\n")
    while (separador >= 0) {
      const bloco = buffer.slice(0, separador)
      buffer = buffer.slice(separador + 2)
      processarBlocoSse(bloco, aoReceber)
      separador = buffer.indexOf("\n\n")
    }
  }
}

function processarBlocoSse(
  bloco: string,
  aoReceber: (notificacao: Notificacao, tipo: TipoEventoNotificacao) => void,
) {
  let evento = "message"
  const dados: string[] = []
  for (const linha of bloco.split(/\r?\n/)) {
    if (linha.startsWith("event:")) {
      evento = linha.slice("event:".length).trim()
    } else if (linha.startsWith("data:")) {
      dados.push(linha.slice("data:".length).trim())
    }
  }
  if (dados.length === 0) return
  if (evento !== "notificacao" && evento !== "notificacao-atualizada") return

  try {
    const notificacao = JSON.parse(dados.join("\n")) as Notificacao
    aoReceber(notificacao, evento === "notificacao" ? "nova" : "atualizada")
  } catch {
    // Evento malformado é ignorado; a próxima notificação/polling corrige o estado.
  }
}
